import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { fetchStripePayoutDetails } from "../../../../../services/AdminServices/PaymentsAdmin/payments.services";
import { useTranslation } from "react-i18next";

const StripePayouts = ({ setSnack }) => {
  const { t } = useTranslation("admin");
  const [payoutId, setPayoutId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const fetchPayoutTaxes = async () => {
    if (!payoutId.trim()) {
      setSnack((snack) => ({
        ...snack,
        open: true,
        text: t("payments.pleaseEnterAPayoutId"),
        severity: "warning",
      }));
      return;
    }

    try {
      setLoading(true);
      setData(null);

      const response = await fetchStripePayoutDetails(payoutId);

      // On failure the service returns data: null, so this must not fall through.
      if (response.error) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text:
              response.error.response?.data?.message ||
              response.error.message ||
              t("common:messages.somethingWentWrong"),
            severity: "error",
          };
        });
        return;
      }

      const result = response.data?.result;
      if (!result) {
        setSnack((snack) => {
          return {
            ...snack,
            open: true,
            text: t("payments.noPayoutFoundForThat"),
            severity: "warning",
          };
        });
        return;
      }

      setData(result);

      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("payments.payoutDataFetchedSuccessfully"),
          severity: "success",
        };
      });
    } catch (error) {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: error.message || t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!data) return;

    const doc = new jsPDF();

    // ===== Title =====
    doc.setFontSize(18);
    doc.text("Stripe Payout Tax Report", 14, 20);

    doc.setFontSize(12);
    doc.text(`Payout ID: ${payoutId}`, 14, 30);
    doc.text(`Total Tax: $${Number(data.total_tax).toFixed(2)}`, 14, 38);

    // =========================
    // Tax Breakdown Table
    // =========================
    autoTable(doc, {
      startY: 45,
      head: [["Tax Name", "Tax Charge", "Shipment Total", "Shipments"]],
      body: data.tax_breakdown.map((tax) => [
        tax.NameEn,
        `$${Number(tax.Charge).toFixed(2)}`,
        `$${Number(tax.sub_totals).toFixed(2)}`,
        tax.shipment_ids?.join(", ") || "-",
      ]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    let finalY = doc.lastAutoTable.finalY + 10;

    // =========================
    // Transaction Breakdown
    // =========================
    doc.setFontSize(14);
    doc.text("Transaction Breakdown", 14, finalY);
    finalY += 8;

    data.transaction_breakdown.forEach((txn, index) => {
      // Add page if needed
      if (finalY > 270) {
        doc.addPage();
        finalY = 20;
      }

      doc.setFontSize(11);
      doc.text(`Shipment ID: ${txn.shipment_id}`, 14, finalY);
      finalY += 6;

      doc.text(`Tracking: ${txn.tracking_id}`, 14, finalY);
      finalY += 6;

      doc.text(
        `Shipment Charge: $${Number(txn.sub_total).toFixed(2)}`,
        14,
        finalY,
      );
      finalY += 8;

      if (txn.tax_charges?.length > 0) {
        autoTable(doc, {
          startY: finalY,
          head: [["Tax Name", "Charge"]],
          body: txn.tax_charges.map((tax) => [
            tax.NameEn,
            `$${Number(tax.Charge).toFixed(2)}`,
          ]),
          styles: { fontSize: 9 },
          headStyles: { fillColor: [52, 73, 94] },
        });

        finalY = doc.lastAutoTable.finalY + 10;
      } else {
        doc.text("No tax applied", 20, finalY);
        finalY += 10;
      }
    });

    doc.save(`Payout_${payoutId}.pdf`);
  };

  return (
    <div className="min-h-screen bg-ui-surface-sunken p-6">
      <div className="max-w-6xl mx-auto bg-ui-surface shadow-xl rounded-xl p-6">
        <h1 className="text-2xl font-bold mb-6 text-ui-text">
          {t("payments.stripePayoutTaxDetails")}
        </h1>

        {/* Input Section */}
        <div className="flex gap-3 mb-6">
          <input
            type="text"
            value={payoutId}
            onChange={(e) => {
              setPayoutId(e.target.value);
              setData(null);
            }}
            placeholder={t("payments.enterPayoutId")}
            className="flex-1 border border-ui-border rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand outline-none"
          />
          <button
            onClick={fetchPayoutTaxes}
            className="bg-brand text-white px-6 py-2 rounded-lg hover:bg-brand-hover transition"
          >
            {loading ? t("payments.loading") : t("payments.fetch")}
          </button>

          {data && (
            <button
              onClick={exportToPDF}
              className="bg-state-success text-white px-6 py-2 rounded-lg hover:bg-state-success transition"
            >
              {t("payments.exportPdf")}
            </button>
          )}
        </div>

        {/* Results */}
        {data && (
          <div className="space-y-8">
            {/* Total Tax */}
            <div className="bg-brand-light p-4 rounded-lg">
              <h2 className="text-lg font-semibold text-blue-800">{t("payments.totalTax")}</h2>
              <p className="text-2xl font-bold text-brand">
                ${data.total_tax}
              </p>
            </div>

            {/* Tax Breakdown */}
            <div>
              <h2 className="text-lg font-semibold mb-3 text-ui-text">
                {t("payments.taxBreakdown")}
              </h2>
              <table className="min-w-full border border-ui-border rounded-lg overflow-hidden">
                <thead className="bg-ui-surface-sunken">
                  <tr>
                    <th className="text-left p-3">{t("payments.taxName")}</th>
                    <th className="text-left p-3">{t("payments.taxCharge")}</th>
                    <th className="text-left p-3">{t("payments.shipmentTotal")}</th>
                    <th className="text-left p-3">{t("payments.shipments")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.tax_breakdown.map((tax, index) => (
                    <tr key={index} className="border-t">
                      <td className="p-3">{tax.NameEn}</td>
                      <td className="p-3 font-medium">${tax.Charge}</td>
                      <td className="p-3 font-medium">${tax.sub_totals}</td>
                      <td className="p-3 font-medium">
                        {tax.shipment_ids?.join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Transaction Breakdown */}
            <div>
              <h2 className="text-lg font-semibold mb-3 text-ui-text">
                {t("payments.transactionBreakdown")}
              </h2>

              <div className="space-y-4">
                {data.transaction_breakdown.map((txn, index) => (
                  <div key={index} className="border rounded-lg p-4 bg-ui-surface-sunken">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium">
                        {t("payments.shipmentId")} {txn.shipment_id}
                      </span>
                      <span className="text-sm text-ui-text-muted">
                        {t("payments.tracking")} {txn.tracking_id}
                      </span>
                      <span className="text-sm text-ui-text-muted">
                        {t("payments.shipmentCharge", { amount: txn.sub_total })}
                      </span>
                    </div>

                    {txn.tax_charges.length > 0 ? (
                      <ul className="list-disc list-inside text-sm">
                        {txn.tax_charges.map((tax, i) => (
                          <li key={i}>
                            {tax.NameEn} - ${tax.Charge}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-ui-text-muted">{t("payments.noTaxApplied")}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StripePayouts;
